import classnames from 'classnames';
import { nb } from 'date-fns/locale/nb';
import React, { Ref, useRef, useState, useMemo, useCallback, useEffect, useId } from 'react';

import { format, getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { Calendar, CalendarProps } from '@eika/calendar';
import { useDetectClickOutside } from '@eika/hooks';
import { Icon, IconTypes } from '@eika/icon';
import { Input, InputLabel } from '@eika/input';
import { ErrorLabel } from '@eika/label';

import { DateInputProps } from './DateInputTypes';
import { validateDate, validateDateRange, dateErrorMessages, parseDateInput } from './utils/dateParser';

import './DateInput.scss';

const DATE_FORMAT = 'dd.MM.yyyy';

const DateInput = React.forwardRef(
    (
        {
            date,
            onChange,
            placeholder,
            minDate,
            maxDate,
            label,
            sublabel,
            error,
            required = false,
            errorMessages = dateErrorMessages,
            compareTime = false,
            withHiddenScreenReaderText = true,
            rightAlignCalendarPopup = false,
            alwaysOpenCalendar = false,
            containerClassName,
            alwaysShowYearInCalendar = false,
            ...rest
        }: DateInputProps,
        ref: Ref<HTMLInputElement>,
    ): JSX.Element => {
        const id = useId();
        const inputId = `date-input--input-${id}`;
        const calendarId = `date-input--calendar-${id}`;
        const labelId = `date-input--label-${id}`;
        const descriptionId = `date-input--description-${id}`;
        const [value, setValue] = useState<string>(date ? format(date, DATE_FORMAT) : '');

        const [showDatePicker, setShowDatePicker] = useState(false);
        const [dateErrors, setDateErrors] = useState<string | undefined>();

        const [viewDate, setViewDate] = useState<ZonedDate | undefined>(date);

        const containerRef = useRef<HTMLDivElement | null>(null);

        useEffect(() => {
            if (date) {
                setViewDate(date);
                setDateErrors(undefined);
                setValue(format(date, DATE_FORMAT));
            }
        }, [date]);

        const screenReaderLabel = useMemo(() => {
            const message = 'Velg dato.';

            if (date) {
                return `${message} Valgt dato er ${format(date, 'EEEE d. MMMM yyyy', nb)}`;
            }

            return message;
        }, [date]);

        const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            setValue(e.currentTarget.value);
            setDateErrors(undefined);
        };

        const setErrorAndClearDate = useCallback(
            (error?: string) => {
                setDateErrors(error);
                onChange(undefined);
            },
            [onChange],
        );

        const tryApplyDate = (dateString: string) => {
            const validInput = validateDate(dateString, DATE_FORMAT);
            try {
                const noDate = !dateString || dateString === '';
                if (!validInput && !noDate) {
                    setErrorAndClearDate(errorMessages.invalid);
                    return;
                }

                if (noDate) {
                    const error = required ? errorMessages.required : undefined;
                    setErrorAndClearDate(error);
                    return;
                }

                const parsedDate = parseDateInput(dateString, DATE_FORMAT);
                const zonedDate = getZonedDate(parsedDate);
                const errors = validateDateRange(zonedDate, minDate, maxDate, errorMessages, !compareTime);
                if (errors) {
                    setErrorAndClearDate(errors);
                    return;
                }

                setDateErrors(undefined);
                setViewDate(zonedDate);
                onChange(zonedDate);
            } catch {
                onChange(undefined);
            }
        };

        const onInputBlur = <TElementType = HTMLInputElement,>(e: React.FocusEvent<TElementType>) => {
            if (!containerRef.current?.contains(e.relatedTarget)) {
                const dateString =
                    'value' in e.currentTarget &&
                    typeof e.currentTarget.value === 'string' &&
                    e.currentTarget.value.length
                        ? (e.currentTarget.value as unknown as string)
                        : value;
                tryApplyDate(dateString);
            }
        };

        const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.code === 'Enter') {
                tryApplyDate(e.currentTarget.value);
            }
            if (e.code === 'ArrowDown' && !showDatePicker) {
                e.stopPropagation();
                setShowDatePicker(true);
                return;
            }
            if (e.code === 'ArrowUp' && !showDatePicker) {
                e.stopPropagation();
                setShowDatePicker(false);
            }
        };

        const setDate = (date: ZonedDate | undefined) => {
            setValue(date ? format(date, DATE_FORMAT) : '');
            setShowDatePicker(false);
            onChange(date);
        };

        useDetectClickOutside(containerRef, () => {
            setShowDatePicker(false);
        });

        const isError = useMemo(() => !!error || !!dateErrors, [error, dateErrors]);

        const calendarProps: CalendarProps = {
            selected: date,
            onClose: () => setShowDatePicker(false),
            onSelect: setDate,
            maxDate,
            minDate,
            alwaysShowYear: alwaysShowYearInCalendar,
        };

        return (
            <>
                <InputLabel
                    id={labelId}
                    label={label}
                    sublabel={sublabel}
                    className={classnames('sgw-date-input__wrapper', containerClassName)}
                >
                    {withHiddenScreenReaderText && (
                        <span id={descriptionId} className="screen-reader-text">
                            {screenReaderLabel}
                        </span>
                    )}
                    <div
                        className={classnames('sgw-date-input', {
                            'date-picker-visible': showDatePicker,
                            'right-align-calendar': rightAlignCalendarPopup,
                        })}
                        ref={containerRef}
                    >
                        <Input
                            id={inputId}
                            placeholder={placeholder || DATE_FORMAT}
                            className={classnames({ 'date-picker-visible': showDatePicker })}
                            onChange={onInputChange}
                            onBlur={onInputBlur}
                            value={value}
                            ref={ref}
                            onKeyDown={onInputKeyDown}
                            error={isError}
                            aria-labelledby={label ? labelId : undefined}
                            aria-describedby={withHiddenScreenReaderText ? descriptionId : undefined}
                            required={required}
                            {...rest}
                        />
                        {alwaysOpenCalendar ? (
                            <div className="calendar-button icon-only">
                                <Icon type={IconTypes.CALENDAR_24} />
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="calendar-button"
                                onClick={() => setShowDatePicker((prev) => !prev)}
                                onBlur={onInputBlur}
                                aria-haspopup="true"
                                aria-expanded={showDatePicker}
                                aria-controls={calendarId}
                                aria-label="Åpne kalender"
                            >
                                <Icon type={IconTypes.CALENDAR_24} />
                            </button>
                        )}

                        {!alwaysOpenCalendar && (
                            <div id={calendarId} role="dialog">
                                <Calendar
                                    {...calendarProps}
                                    show={showDatePicker}
                                    popup
                                    alignPopupRight={rightAlignCalendarPopup}
                                />
                            </div>
                        )}
                    </div>
                    {isError && <ErrorLabel className="sgw-date-input__error" text={error ?? dateErrors} />}
                </InputLabel>
                {alwaysOpenCalendar && (
                    <div id={calendarId} className="sgw-date-input__calendar-wrapper">
                        <Calendar {...calendarProps} className="sgw-date-input__calendar" show view={viewDate} />
                    </div>
                )}
            </>
        );
    },
);

export default DateInput;
